import { normalizeLoginType } from './auth';
import { normalizeRolePermissionsForDisplay } from './permissionGroups';

export const normalizePermissions = (permissions) => {
  if (!Array.isArray(permissions)) return [];
  return permissions.filter((item) => typeof item === 'string');
};

export const permissionsAreEqual = (left = [], right = []) => {
  const a = normalizePermissions(left);
  const b = normalizePermissions(right);
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((item) => setB.has(item));
};

export const hasPermission = (permissions, permissionKey) => {
  const list = normalizePermissions(permissions);
  return list.includes(permissionKey);
};

export const hasAnyPermission = (permissions, keys = []) => {
  const list = normalizePermissions(permissions);
  return keys.some((key) => list.includes(key));
};

export const isTenantSubUser = (userData) => {
  const userInfo = userData?.user?.user_data || {};

  if (userInfo.auth_type === 'tenant') return true;
  if (userInfo.tenant_user_id) return true;
  if (userInfo.tenant_username) return true;
  if (userInfo.master_name && userInfo.tenant_name) return true;
  if (userData?.auth_type === 'tenant') return true;
  if (userData?.tenant_user_id) return true;

  return false;
};

/**
 * Full-access users bypass all permission checks:
 * - Super-admin (loginType admin / user_type 1)
 * - Master DB seller login (email login, auth_type master)
 * - Tenant users with Owner system role
 */
export const hasFullAccess = (userData) => {
  if (!userData?.token) return false;

  const loginType = normalizeLoginType(userData?.loginType);
  if (loginType === 'admin') return true;

  const userInfo = userData?.user?.user_data || {};

  if (isTenantSubUser(userData)) {
    return isSystemRoleName(userInfo.role_name);
  }

  if (userInfo.auth_type === 'master') return true;

  // Master seller email login (user_type 3, not a tenant sub-user)
  if (loginType === 'user' && !isTenantSubUser(userData)) {
    return true;
  }

  return false;
};

/** @deprecated Use hasFullAccess */
export const isOwnerOrAdminLogin = hasFullAccess;

export const getStoredPermissions = (userData) => {
  const topLevel = normalizePermissions(userData?.permissions);
  const nested = normalizePermissions(userData?.user?.user_data?.permissions);

  // Top-level permissions are authoritative after SET_PERMISSIONS / login sync.
  if (topLevel.length > 0) {
    return topLevel;
  }

  return nested;
};

/** Raw RBAC permissions for sidebar/route checks — no legacy expansion. */
export const getMenuPermissions = (userData, permissionsOverride = null) => {
  if (Array.isArray(permissionsOverride)) {
    return normalizePermissions(permissionsOverride);
  }

  return getStoredPermissions(userData);
};

export const getUserPermissions = (userData, permissionsOverride = null) => {
  const fromLogin =
    permissionsOverride != null
      ? normalizePermissions(permissionsOverride)
      : getStoredPermissions(userData);

  if (permissionsOverride != null || !hasFullAccess(userData)) {
    return normalizeRolePermissionsForDisplay(fromLogin);
  }

  return fromLogin;
};

export const shouldSyncPermissionsOnLoad = (userData) => {
  if (!userData?.token) return false;

  const loginType = normalizeLoginType(userData?.loginType);
  if (loginType !== 'user') return false;

  return isTenantSubUser(userData);
};

/** Staff users whose sidebar must wait for a fresh /rbac/my-permissions response. */
export const needsPermissionGatedMenu = (userData) =>
  shouldSyncPermissionsOnLoad(userData);

export const shouldHydratePermissions = (userData) =>
  needsPermissionGatedMenu(userData);

export const getPermissionsSignature = (userData, permissionsOverride = null) =>
  normalizePermissions(getUserPermissions(userData, permissionsOverride))
    .slice()
    .sort()
    .join('|');

export const canAccess = (userData, permission, permissions = []) => {
  const keys = permission ? [permission, ...permissions] : permissions;
  if (!keys.length) return true;

  if (isTenantSubUser(userData) && !hasFullAccess(userData)) {
    return hasAnyPermission(getMenuPermissions(userData), keys);
  }

  if (hasFullAccess(userData)) return true;

  return hasAnyPermission(getUserPermissions(userData), keys);
};

export const canAccessWithPermissions = (
  userData,
  permission,
  permissions = [],
  permissionsOverride = null
) => {
  const keys = permission ? [permission, ...permissions] : permissions;
  if (!keys.length) return true;

  if (
    shouldSyncPermissionsOnLoad(userData) &&
    permissionsOverride !== null &&
    Array.isArray(permissionsOverride)
  ) {
    return hasAnyPermission(getMenuPermissions(userData, permissionsOverride), keys);
  }

  return canAccess(userData, permission, permissions);
};

/** Strict action checks — uses stored permissions exactly as returned by RBAC. */
export const canAccessAction = (
  userData,
  permission,
  permissions = [],
  permissionsOverride = null
) => {
  if (hasFullAccess(userData)) return true;

  const keys = permission ? [permission, ...permissions] : permissions;
  if (!keys.length) return true;

  const granted =
    permissionsOverride !== null && Array.isArray(permissionsOverride)
      ? normalizePermissions(permissionsOverride)
      : getStoredPermissions(userData);

  return hasAnyPermission(granted, keys);
};

/** Strict check — user must have every listed permission. */
export const canAccessAllActions = (userData, permissions = [], permissionsOverride = null) => {
  if (hasFullAccess(userData)) return true;

  const keys = normalizePermissions(permissions);
  if (!keys.length) return true;

  const granted =
    permissionsOverride !== null && Array.isArray(permissionsOverride)
      ? normalizePermissions(permissionsOverride)
      : getStoredPermissions(userData);

  return keys.every((key) => granted.includes(key));
};

/** Product create requires products.create plus catalog, brand, and category view access. */
export const canCreateProduct = (userData, permissionsOverride = null) =>
  canAccessAllActions(
    userData,
    [
      'products.create',
      'categories.view',
      'brands.view',
      'catalog.view',
    ],
    permissionsOverride
  );

/** Product add/edit form requires update or create plus dependency permissions. */
export const canAccessProductForm = (
  userData,
  { isEditMode = false, permissionsOverride = null } = {}
) => {
  const primary = isEditMode ? 'products.update' : 'products.create';
  return canAccessAllActions(
    userData,
    [primary, 'categories.view', 'brands.view', 'catalog.view'],
    permissionsOverride
  );
};

export const getMissingProductCreatePermissions = (
  userData,
  permissionsOverride = null
) => {
  if (hasFullAccess(userData)) return [];

  const granted =
    permissionsOverride !== null && Array.isArray(permissionsOverride)
      ? normalizePermissions(permissionsOverride)
      : getStoredPermissions(userData);

  return [
    'products.create',
    'categories.view',
    'brands.view',
    'catalog.view',
  ].filter((key) => !granted.includes(key));
};

/** Add product to catalog requires products.add_to_catalog plus catalog.view. */
export const canAddProductToCatalog = (userData, permissionsOverride = null) =>
  canAccessAllActions(
    userData,
    ['products.add_to_catalog', 'catalog.view'],
    permissionsOverride
  );

export const getTenantUserId = (userData) => {
  const userInfo = userData?.user?.user_data || {};
  const id = userInfo.tenant_user_id;
  return id != null && id !== '' ? Number(id) : null;
};

export const getTenantRoleId = (userData) => {
  const userInfo = userData?.user?.user_data || {};
  const id = userInfo.role_id;
  return id != null && id !== '' ? Number(id) : null;
};

/** Staff tenant sub-users (not Owner/master). */
export const isStaffRestrictedUser = (userData) =>
  isTenantSubUser(userData) && !hasFullAccess(userData);

const SYSTEM_ROLE_NAMES = new Set(['owner']);

const LOCKED_DEFAULT_ROLE_NAMES = new Set([
  'operations manager',
  'analyst',
  'virtual assistant (va)',
]);

const DEFAULT_ROLE_NAMES = new Set([
  'admin',
  ...LOCKED_DEFAULT_ROLE_NAMES,
]);

export const normalizeRoleName = (roleName) =>
  String(roleName || '').trim().toLowerCase();

export const isSystemRoleName = (roleName) =>
  SYSTEM_ROLE_NAMES.has(normalizeRoleName(roleName));

export const isDefaultRoleName = (roleName) =>
  DEFAULT_ROLE_NAMES.has(normalizeRoleName(roleName));

export const isLockedDefaultRoleName = (roleName) =>
  LOCKED_DEFAULT_ROLE_NAMES.has(normalizeRoleName(roleName));

export const isPredefinedRoleRecord = (record) =>
  isDefaultRoleName(record?.name);

export const isProtectedUserRecord = (record) =>
  isSystemRoleName(record?.role_name);

export const isProtectedRoleRecord = (record) =>
  isSystemRoleName(record?.name);

export const isSelfUserRecord = (userData, userId) => {
  const selfId = getTenantUserId(userData);
  if (selfId == null || userId == null || userId === '') return false;
  return Number(userId) === selfId;
};

export const isSelfRoleRecord = (userData, roleId) => {
  const selfRoleId = getTenantRoleId(userData);
  if (selfRoleId == null || roleId == null || roleId === '') return false;
  return Number(roleId) === selfRoleId;
};

export const getStaffUserBlockTooltipKey = (userData, record) => {
  if (!isStaffRestrictedUser(userData)) return null;
  if (isSelfUserRecord(userData, record?.id)) {
    return 'permissions.selfUserActionTooltip';
  }
  if (isProtectedUserRecord(record)) {
    return 'permissions.protectedUserActionTooltip';
  }
  return null;
};

export const getStaffUserStatusBlockTooltipKey = (userData, record) => {
  if (!isStaffRestrictedUser(userData)) return null;
  if (isSelfUserRecord(userData, record?.id)) {
    return 'permissions.selfUserStatusTooltip';
  }
  if (isProtectedUserRecord(record)) {
    return 'permissions.protectedUserStatusTooltip';
  }
  return null;
};

/** Owner rows are locked for everyone; staff also cannot edit themselves. */
export const isUserManagementActionBlocked = (userData, record) => {
  if (isProtectedUserRecord(record)) return true;
  return isStaffUserActionBlocked(userData, record);
};

export const getUserManagementActionTooltipKey = (userData, record) => {
  if (isStaffRestrictedUser(userData) && isSelfUserRecord(userData, record?.id)) {
    return 'permissions.selfUserActionTooltip';
  }
  if (isProtectedUserRecord(record)) {
    return 'permissions.protectedUserActionTooltip';
  }
  return null;
};

export const getUserManagementStatusTooltipKey = (userData, record) => {
  if (isStaffRestrictedUser(userData) && isSelfUserRecord(userData, record?.id)) {
    return 'permissions.selfUserStatusTooltip';
  }
  if (isProtectedUserRecord(record)) {
    return 'permissions.protectedUserStatusTooltip';
  }
  return null;
};

export const getUserManagementRoleTooltipKey = (userData, record) => {
  if (isProtectedUserRecord(record)) {
    return 'permissions.protectedUserRoleTooltip';
  }
  return null;
};

/** Owner role is locked for everyone; staff also cannot edit their own role. */
export const isRoleManagementActionBlocked = (userData, record) => {
  if (isProtectedRoleRecord(record)) return true;
  return isStaffRoleActionBlocked(userData, record);
};

export const getRoleManagementActionTooltipKey = (userData, record) => {
  if (isStaffRestrictedUser(userData) && isSelfRoleRecord(userData, record?.id)) {
    return 'permissions.selfRoleActionTooltip';
  }
  if (isProtectedRoleRecord(record)) {
    return 'permissions.protectedRoleActionTooltip';
  }
  return null;
};

export const getRoleAssignedUsersCount = (record = {}) => {
  const count = Number(record.users ?? record.users_count ?? 0);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

export const isRoleAssignedToUsers = (record = {}) =>
  getRoleAssignedUsersCount(record) > 0;

export const isRoleDeleteBlocked = (userData, record = {}) =>
  isRoleManagementActionBlocked(userData, record) ||
  isPredefinedRoleRecord(record) ||
  isRoleAssignedToUsers(record);

export const getRoleDeleteBlockedTooltipKey = (userData, record = {}) => {
  if (isPredefinedRoleRecord(record)) {
    return 'permissions.predefinedRoleDeleteTooltip';
  }
  if (isRoleAssignedToUsers(record)) {
    return 'permissions.roleAssignedUsersDeleteTooltip';
  }
  return getRoleManagementActionTooltipKey(userData, record);
};

export const getStaffRoleBlockTooltipKey = (userData, record) => {
  if (!isStaffRestrictedUser(userData)) return null;
  if (isSelfRoleRecord(userData, record?.id)) {
    return 'permissions.selfRoleActionTooltip';
  }
  if (isProtectedRoleRecord(record)) {
    return 'permissions.protectedRoleActionTooltip';
  }
  return null;
};

export const isStaffUserActionBlocked = (userData, recordOrId) => {
  if (!isStaffRestrictedUser(userData)) return false;
  const record =
    typeof recordOrId === 'object' && recordOrId !== null
      ? recordOrId
      : { id: recordOrId };
  return isSelfUserRecord(userData, record.id) || isProtectedUserRecord(record);
};

export const isStaffRoleActionBlocked = (userData, recordOrId) => {
  if (!isStaffRestrictedUser(userData)) return false;
  const record =
    typeof recordOrId === 'object' && recordOrId !== null
      ? recordOrId
      : { id: recordOrId };
  return isSelfRoleRecord(userData, record.id) || isProtectedRoleRecord(record);
};

/** @deprecated Use isStaffUserActionBlocked */
export const isSelfUserActionBlocked = (userData, recordOrId) =>
  isStaffUserActionBlocked(userData, recordOrId);

/** @deprecated Use isStaffRoleActionBlocked */
export const isSelfRoleActionBlocked = (userData, roleId) =>
  isStaffRoleActionBlocked(userData, roleId);
